import {
  AccountQueryKeysEnum,
  CUSTOM_PLAN_PAGE_SIZE,
} from "@/entities/account/account.constants"
import { Tokens } from "@/entities/auth/auth.types"
import { apiWithoutAuth } from "@/shared/lib/axios/axios.api"
import { MP_AUTH_API_PREFIX } from "@/shared/lib/axios/axios.constants"
import { handleAxiosError } from "@/shared/lib/axios/axios.lib"
import {
  CustomUseMutationOptions,
  FIVE_MINUTES,
  ONE_DAY,
  THREE_MINUTES,
} from "@/shared/lib/react-query/react-query.lib"
import { createTokens } from "@/shared/lib/token/token"
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query"
import axios from "axios"
import { jwtDecode } from "jwt-decode"
import { useAuthStore } from "@/entities/auth/auth.model"
import {
  changeLanguage,
  deleteAccount,
  editProfile,
  getAccountInfo,
  getBillingInfo,
  getCountryCode,
  getCurrentSubscriptionContractInfo,
  getCustomPlanInfo,
  getSubscriptionInfo,
  getUserInfo,
} from "./account.api"
import {
  BillingInfo,
  ContractInfo,
  CustomPlanFilter,
  CustomPlanInfo,
  DecodedAccessToken,
  SubscriptionInfo,
  UserInfo,
} from "./account.types"
import { camelizeKeys } from "humps"

export const useAccountInfoQuery = ({
  accountId,
  enabled,
}: {
  enabled: boolean
  accountId: string
}) => {
  return useQuery<UserInfo>({
    queryKey: [AccountQueryKeysEnum.FetchAccount],
    queryFn: () => getAccountInfo({ accountId }),
    enabled,
  })
}

export const useBillingInfoQuery = ({
  enabled,
  refetchOnWindowFocus = true,
}: {
  enabled?: boolean
  refetchOnWindowFocus?: boolean
}) => {
  return useQuery<BillingInfo>({
    queryKey: [AccountQueryKeysEnum.FetchBillingInfo],
    queryFn: getBillingInfo,
    enabled,
    staleTime: 0,
    refetchOnWindowFocus,
  })
}

export const useUserInfoQuery = ({
  enabled,
  staleTime = 0,
}: {
  enabled: boolean
  staleTime?: number
}) => {
  return useQuery<UserInfo>({
    queryKey: [AccountQueryKeysEnum.FetchUserInfo],
    queryFn: getUserInfo,
    staleTime,
    enabled,
  })
}

export const useContractInfoQuery = ({
  enabled,
  refetchOnWindowFocus = true,
}: {
  enabled?: boolean
  refetchOnWindowFocus?: boolean
}) => {
  return useQuery<ContractInfo | null>({
    queryKey: [AccountQueryKeysEnum.FetchContractInfo],
    queryFn: getCurrentSubscriptionContractInfo,
    enabled,
    staleTime: THREE_MINUTES,
    refetchOnWindowFocus: (query) =>
      query.state.data !== null && refetchOnWindowFocus,
  })
}

export const useIpQuery = () => {
  return useQuery<{ ip: string }>({
    queryKey: [AccountQueryKeysEnum.FetchIp],
    queryFn: async () => {
      const response = await axios.get("https://jsonip.com")

      return response.data
    },
    staleTime: FIVE_MINUTES,
  })
}

export const useCountryCodeQuery = ({
  ip,
  enabled,
}: {
  ip: string
  enabled?: boolean
}) => {
  return useQuery<string>({
    queryKey: [AccountQueryKeysEnum.FetchCountryCode],
    queryFn: () => getCountryCode({ ip }),
    enabled,
    staleTime: FIVE_MINUTES,
  })
}

export const useCustomPlanInfoQuery = (filter: CustomPlanFilter) => {
  return useInfiniteQuery<CustomPlanInfo[]>({
    queryKey: [AccountQueryKeysEnum.FetchCustomPlanInfo, filter],
    queryFn: ({ pageParam = 0 }) => getCustomPlanInfo({ pageParam, filter }),
    getNextPageParam: (lastPage, pages) =>
      lastPage.length < CUSTOM_PLAN_PAGE_SIZE ? undefined : pages.length,
    initialPageParam: 0,
    staleTime: ONE_DAY,
  })
}

export const useSubscriptionInfoQuery = ({
  enabled,
  refetchOnWindowFocus = true,
  subscriptionId,
}: {
  enabled?: boolean
  refetchOnWindowFocus?: boolean
  subscriptionId: string
}) => {
  return useQuery<SubscriptionInfo>({
    queryKey: [AccountQueryKeysEnum.FetchSubscriptionInfo],
    queryFn: () => getSubscriptionInfo({ subscriptionId }),
    enabled,
    staleTime: 0,
    refetchOnWindowFocus,
  })
}

// mutations
export const useEditProfileMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: editProfile,
    onSuccess: () => {
      if (onSuccess) onSuccess()
    },
    onError,
    onSettled,
  })
}

export const useDeleteAccountMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: deleteAccount,
    onSuccess,
    onError,
    onSettled,
  })
}

export const useChangeLanguageMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  return useMutation({
    mutationFn: changeLanguage,
    onSuccess,
    onError,
    onSettled,
  })
}

export const useHandleResetUserDataMutation = ({
  onSuccess,
  onError,
  onSettled,
}: CustomUseMutationOptions) => {
  const { handleSetUserData } = useAuthStore()

  const handleResetUserDataWithContext = async ({
    token,
  }: {
    token: string
  }) => {
    try {
      const response = await apiWithoutAuth.post<Tokens>(
        `${MP_AUTH_API_PREFIX}/refresh`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      const { accessToken, refreshToken } = camelizeKeys(
        response.data
      ) as Tokens
      createTokens(accessToken, refreshToken)

      const decodedData = jwtDecode<DecodedAccessToken>(accessToken)
      handleSetUserData(decodedData)

      return response.data
    } catch (error) {
      handleAxiosError(error)
    }
  }

  return useMutation({
    mutationFn: handleResetUserDataWithContext,
    onSuccess: () => {
      if (onSuccess) onSuccess()
    },
    onError,
    onSettled,
  })
}
